import type { ActiveOrderQuery, SubscribeToEcomailInput } from '#build/graphql-operations'
import { SubscribeToEcomailError } from '~/constants/errors'

type EcotrackArgs = 'trackPageView' | 'setUserIdFromLocation' | 'addTrans' | 'addItem' | 'trackTrans' | 'setUserId' | 'trackUnstructEvent' | 'trackStructEvent'

declare global {
  interface Window { ecotrack: (arg: EcotrackArgs, ...args: any) => void }
}

const APP_ID = 'exalted'

export default defineNuxtPlugin(async () => {
  const toFixed = (amount: string | number) => (Number(amount) / 100).toFixed(2)

  const init = () => {
    const scriptContent = `;(function(p,l,o,w,i,n,g){if(!p[i]){p.GlobalSnowplowNamespace=p.GlobalSnowplowNamespace||[];
p.GlobalSnowplowNamespace.push(i);p[i]=function(){(p[i].q=p[i].q||[]).push(arguments)
};p[i].q=p[i].q||[];n=l.createElement(o);g=l.getElementsByTagName(o)[0];n.async=1;
n.src=w;g.parentNode.insertBefore(n,g)}}(window,document,"script","//d70shl7vidtft.cloudfront.net/ecmtr-2.4.2.js","ecotrack"));
window.ecotrack('newTracker', 'cf', 'd2dpiwfhf3tz0r.cloudfront.net', { /* Initialise a tracker */
appId: '${APP_ID}'
});
`
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.innerHTML = scriptContent
    document.head.appendChild(script)
  }

  const { activeOrder } = useActiveOrder()
  const { activeCustomer } = useAuth()

  const customerEmail = ref('')

  init()

  const subscribeUser = async (input: SubscribeToEcomailInput) => {
    const { data, errors } = await useGraphqlMutation('subscribeToEcomail', { input })
    if (data.subscribeToEcomail?.__typename === 'SubscribeToEcomailError') {
      throw new SubscribeToEcomailError(data.subscribeToEcomail.message)
    }
    if (errors.length) {
      throw new Error(errors[0].message)
    }
  }

  const trackCartChange = async (activeOrder: ActiveOrderQuery['activeOrder']) => {
    if (!customerEmail.value)
      return

    const products = activeOrder?.lines?.reduce<any[]>((acc, line) => {
      const { quantity, id, ...rest } = line
      Array.from({ length: quantity }).fill('').forEach(() => acc.push({
        productId: rest?.productVariant?.product?.id,
        img_url: rest?.featuredAsset?.preview,
        url: `${window.location.host}/products/${rest?.productVariant?.product?.id}`,
        name: rest?.productVariant?.product?.name,
        price: toFixed(rest?.productVariant?.price),
      }))
      return acc
    }, [])

    window.ecotrack('trackUnstructEvent', {
      schema: '',
      data: {
        action: 'Basket',
        products,
      },
    })
  }

  // NOTE: order transactions are deliberately NOT tracked from the browser. Vendure's
  // PostOrderIntegrationsPlugin posts them to Ecomail server-side, keyed on the order code.
  // A browser-side `addTrans` used to run here as well; it keyed on the order id, so Ecomail
  // could not dedupe the two and counted every order twice — and being client-side it missed
  // roughly a third of orders anyway.

  const trackLastViewed = async (productId: string) => {
    if (!customerEmail.value)
      return
    window.ecotrack('trackStructEvent', 'ECM_PRODUCT_VIEW', productId)
  }

  watch(activeOrder, (activeOrder, prev) => {
    const email = activeOrder?.customer?.emailAddress

    customerEmail.value = email || ''
    window.ecotrack('setUserId', email)

    if (activeOrder && (activeOrder?.subTotal !== prev?.subTotal)) {
      trackCartChange(activeOrder)
    }
  })

  watch(activeCustomer, (customer) => {
    customerEmail.value = customer?.emailAddress || ''
    window.ecotrack('setUserId', customer?.emailAddress)
  }, { immediate: true })

  return {
    provide: {
      ecomail: {
        subscribeUser,
        trackCartChange,
        trackLastViewed,
      },
    },
  }
})

interface EcomailPlugin {
  subscribeUser: (input: SubscribeToEcomailInput) => Promise<void>
  trackCartChange: (activeOrder: ActiveOrderQuery['activeOrder']) => void
  trackLastViewed: (productId: string) => void
}

declare module '#app' {
  interface NuxtApp {
    $ecomail: EcomailPlugin
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $ecomail: EcomailPlugin
  }
}
